import { ContentCard, Stack } from '@forwardflow/ui-v2'
import { useFubPonds } from '@forwardflow/queries/fub.queries'
import { useAppStore } from '@forwardflow/store'
import { MultiSelect } from '@mantine/core'
import { useMemo } from 'react'
import { useCampaignFilterContext } from '../hooks'
import { EVENT_OPTIONS } from '../constants'


/**
 * Events + pond-trigger selector for activity campaigns, shown inside the New Campaign wizard.
 * Unlike the edit-page ActivityEventsCard (which auto-saves to the API), this writes straight into
 * the in-progress campaign configuration via context — it's persisted when the campaign is created.
 */
export const ActivityTriggersCard = () => {
   const { configuration, updateConfiguration } = useCampaignFilterContext()
   const { currentOrganization } = useAppStore()

   const criteria = configuration?.activity_campaign_filter_criteria

   const events = criteria?.events?.length ? criteria.events : ['all']
   const pondIds = (criteria?.triggers ?? [])
      .filter((t) => t?.type === 'pond')
      .map((t) => String(t.value))

   const { data: pondsData } = useFubPonds(
      currentOrganization || '',
      1,
      10000,
      true
   )

   const pondOptions = useMemo(() => {
      const seen = new Set<string>()
      const opts: { value: string; label: string }[] = []
      for (const p of (pondsData || []) as {
         pondId: number
         pondName: string
      }[]) {
         const value = String(p.pondId)
         if (seen.has(value)) continue
         seen.add(value)
         opts.push({ value, label: p.pondName })
      }
      return opts
   }, [pondsData])

   // Always write the full criteria object so we never drop events / triggers / lookback.
   const writeCriteria = (next: { events?: string[]; pondIds?: string[] }) => {
      const nextEvents = next.events ?? events
      const nextPondIds = next.pondIds ?? pondIds
      updateConfiguration({
         activity_campaign_filter_criteria: {
            events: nextEvents.length ? nextEvents : ['all'],
            lookback_days: criteria?.lookback_days ?? null,
            triggers: nextPondIds.map((id) => ({
               type: 'pond',
               value: Number(id),
            })),
         },
      })
   }

   // 'Any event' (all) is exclusive: selecting it clears the rest; selecting any other clears 'all'.
   const handleEventsChange = (next: string[]) => {
      const allJustAdded = next.includes('all') && !events.includes('all')
      const cleaned = allJustAdded
         ? ['all']
         : next.length > 1
            ? next.filter((e) => e !== 'all')
            : next
      writeCriteria({ events: cleaned })
   }

   const handlePondsChange = (next: string[]) => {
      writeCriteria({ pondIds: next })
   }

   return (
      <ContentCard className="space-y-4 !p-6">
         <Stack gap={16}>
            <div>
               <p className="text-white font-medium text-base">
                  What should trigger this activity campaign?
               </p>
               <p className="text-white/50 text-sm mt-1">
                  Pick which lead events fire the campaign, and (optionally) ponds
                  the lead must enter to trigger it.
               </p>
            </div>

            <div>
               <p className="text-white/40 text-[10px] uppercase tracking-wide mb-2">
                  Events
               </p>
               <MultiSelect
                  data={EVENT_OPTIONS}
                  value={events}
                  onChange={handleEventsChange}
                  searchable
                  clearable
                  placeholder="Select events"
                  nothingFoundMessage="No events"
                  size="sm"
               />
            </div>

            <div>
               <p className="text-white/40 text-[10px] uppercase tracking-wide mb-2">
                  Pond triggers
               </p>
               <MultiSelect
                  data={pondOptions}
                  value={pondIds}
                  onChange={handlePondsChange}
                  searchable
                  clearable
                  placeholder="Select ponds"
                  nothingFoundMessage="No ponds"
                  size="sm"
               />
            </div>
         </Stack>
      </ContentCard>
   )
}
